<script lang="ts">
    import { firstValueFrom } from 'rxjs';
    import { createEventDispatcher } from 'svelte';
    import type {
        ChartEvent,
        ChartState,
        Datum,
        FormatterPayload,
        Formatters,
        InteractionEvent,
    } from '@bayer-int/portletce-aclibv2';
    import { head, isNotNullable, hasKeysOfType } from '@bayer-int/portletce-shared';
    import { countBy, isString, uniqBy } from 'lodash-es';

    import {
        type ChartRenderProps,
        type NavigationCallbackItem,
        type DataTipNavigationOption,
        type DataTipNavigationOptionsRequestProps,
        PortletQuery,
        type FilterId,
        FilterDataArray,
        type ResolvedListenToDefinitions,
        type Keyword,
    } from 'portlet/models';
    import { getStoreContext, getLoggerContext, getServiceContext } from 'portlet/common/contexts';
    import Lazy from 'common/components/Lazy.svelte';

    import type { Hierarchy, Dimension } from 'portlet/services';
    import { InteractionToFilterService } from 'portlet/services';
    import * as HTML from './html';
    import { createInteractionError } from '../errors';
    import { AppAction } from 'portlet/store/actions';
    import { AppError } from 'portlet/common/errors';
    import { QueryChartInteractionDatapointUtil } from 'portlet/services/InteractionToFilterService/QueryChartInteractionDatapoint';
    import type { ChartRenderPayload } from 'portlet/store/init';

    interface Props {
        isDataConsistent?: boolean;
        anychartConfig: ChartRenderProps;
        dispatchFilters: (filters: FilterDataArray) => void;
        dispatchClearFilterRequest: (items: FilterId[]) => void;
        currentHierarchySetting: Hierarchy | null;
        currentDimensionSetting: Dimension | null;
        currentLinkedHierarchiesSetting: Record<Hierarchy['hierarchyId'], Hierarchy>;
        currentApplyFilterOnExactDrillOnlySetting: boolean | null;
        currentApplyFilterToUpstreamHierarchyLevelsSetting: boolean | null;
        filterDataArray: FilterDataArray;
        queryMetadata: PortletQuery | null;
        listenTo: ResolvedListenToDefinitions;
        dispatchStateChangeEvent: (chartState: ChartState) => void;
        requestDatatipContent: (detail: DataTipNavigationOptionsRequestProps) => Promise<{
            title: string;
            jumpTo: DataTipNavigationOption[];
            showBy: DataTipNavigationOption[];
        }>;
        onDataTipContentClick: (action: NavigationCallbackItem) => void;
        dispatchPerformanceDataTimeChangeEvent: (duration: number) => void;
        filterKeyToEmitFrom: string | null;
        uniqueId: string;
        seriesVisibility: unknown | null;
    }

    let {
        isDataConsistent = true,
        anychartConfig,
        dispatchFilters,
        dispatchClearFilterRequest,
        currentHierarchySetting,
        currentDimensionSetting,
        currentLinkedHierarchiesSetting,
        currentApplyFilterOnExactDrillOnlySetting,
        currentApplyFilterToUpstreamHierarchyLevelsSetting,
        filterDataArray,
        queryMetadata,
        listenTo,
        dispatchStateChangeEvent,
        requestDatatipContent,
        onDataTipContentClick,
        dispatchPerformanceDataTimeChangeEvent,
        uniqueId,
        seriesVisibility,
    }: Props = $props();

    const dispatch = createEventDispatcher();

    const logger$ = getLoggerContext();
    const service$ = getServiceContext();
    const store$ = getStoreContext();

    let logger = $derived(logger$);
    let service = $derived(service$);
    let viewId = $derived(anychartConfig.viewId);

    const formatters: Formatters = {
        tooltip: ({ html, detail }) =>
            enrichDatatipContentRequest(detail)
                .then(requestDatatipContent)
                .then(options => HTML.renderDataTipContent(html, options, onDataTipContentClick)),
    };

    function enrichDatatipContentRequest(
        detail: FormatterPayload['detail'],
    ): Promise<DataTipNavigationOptionsRequestProps> {
        if (!detail.data && !detail.dataRow)
            return Promise.resolve({
                position: detail.position,
                data: null,
                filter: null,
                filters: [],
            });

        const filters$ = $service.interactionService.datumToFilter(
            listenTo,
            filterDataArray,
            queryMetadata,
            {
                currentHierarchySetting,
                currentDimensionSetting,
                currentLinkedHierarchiesSetting,
                currentApplyFilterOnExactDrillOnlySetting,
                currentApplyFilterToUpstreamHierarchyLevelsSetting,
            },
            {
                type: 'QUERY_SELECT',
                cell: detail.data,
                name: QueryChartInteractionDatapointUtil.groupByColumn(
                    detail.data ? [detail.data] : detail.dataRow,
                ),
            },
            detail.dataKeywords,
        );

        return firstValueFrom(filters$)
            .then(filters => {
                const filterOccurrenceCount = countBy(filters, ({ item }) => item);
                const mostRepeatedFilterCount = Math.max(...Object.values(filterOccurrenceCount));

                if (mostRepeatedFilterCount > 1)
                    $logger.warn(
                        `Interaction emitted multiple filters for the same datapoint. Check your configuration. Keeping the first one.`,
                        { filters, detail },
                    );

                return uniqBy(filters, ({ item }) => item);
            })
            .then(filters => ({
                position: detail.position,
                data: detail.data,
                // kept for backwards compatability
                filter: head(filters) || null,
                filters,
            }))
            .catch(error => Promise.reject(createInteractionError({ error, detail })));
    }

    /**
     * All objects that origin from vega can contain extra Symbol properties, which are no longer needed,
     * and can lead to unexpected behaviors like `_.isEqual` seemingly not working
     */
    const removeSymbols = <T extends Record<string, unknown>>(obj: T): T =>
        JSON.parse(JSON.stringify(obj));

    function forwardInteractionEvent(event: ChartEvent) {
        if (!hasKeysOfType(event, isString, 'chartlib')) return;
        if (!hasKeysOfType(event, isNotNullable, 'rawEvent')) return;
        dispatch('chartlib-interaction', {
            detail: {
                chartlib: event.chartlib,
                type: event.type,
                payload: event.rawEvent,
            },
        });
    }

    function handleChartEvents(event: ChartEvent) {
        forwardInteractionEvent(event);
        const logEvents = ['click', 'dblclick', 'brush'];
        if (logEvents.includes(event.type)) {
            // We take out the `rawEvent` because it has too many nested properties which cause `Maximum call stack exceeded` error
            const { rawEvent, ...restEvent } = event.type === 'click' ? event : { rawEvent: null };
            $logger.warn('chart interaction event ', restEvent);
        }
        if ((event.type === 'click' || event.type === 'brush') && event.datum)
            formatAndDispatchEvent(removeSymbols(event.datum), event.keywords || []);
        if (event.type === 'state') dispatchStateChangeEvent(event.data);
        if (event.type === 'change-series-visibility')
            storeSeriesVisibilityInteraction(event, uniqueId, viewId);
        if (event.type === 'change-selected-item') storeSelectedItem(event, uniqueId, viewId);
    }

    function storeSeriesVisibilityInteraction(
        event: InteractionEvent,
        uniqueId: string,
        viewId: number,
    ) {
        $service.sessionStorageService.setSeriesVisibility(
            uniqueId,
            viewId,
            event.chartlib || '',
            event.data,
        );
    }

    function storeSelectedItem(event: InteractionEvent, uniqueId: string, viewId: number) {
        $service.sessionStorageService.setSelectedItem(
            uniqueId,
            viewId,
            event.chartlib || '',
            event.data,
        );
    }

    function formatAndDispatchEvent(datum: Datum, eventKeywords: Keyword[]) {
        if (datum.type === 'CLEAR') return emitClearEvent(eventKeywords);

        const filters$ = $service.interactionService.datumToFilter(
            listenTo,
            filterDataArray,
            queryMetadata,
            {
                currentHierarchySetting,
                currentDimensionSetting,
                currentLinkedHierarchiesSetting,
                currentApplyFilterOnExactDrillOnlySetting,
                currentApplyFilterToUpstreamHierarchyLevelsSetting,
            },
            datum,
            eventKeywords,
        );

        firstValueFrom(filters$)
            .then(filters => filters.length && dispatchFilters(filters))
            .catch(err => {
                $logger.error('Failed to handle user interaction', err);
                $service.errorMonitoringService.captureError(err);
            });
    }

    function emitClearEvent(eventKeywords: Keyword[]) {
        const filterItemsToClear = InteractionToFilterService.matchListenToByKeywordsForClearing(
            listenTo,
            eventKeywords,
        ).map(({ item }) => item);

        if (!filterItemsToClear.length) return;

        return dispatchClearFilterRequest(filterItemsToClear);
    }

    function emitChartRenderedAction(payload: ChartRenderPayload) {
        store$.action$.next(AppAction.portlet.CHART_RENDERED(payload));
    }

    function emitSetDataFromAction({ isDataPossiblyCached }: { isDataPossiblyCached: boolean }) {
        store$.action$.next(
            AppAction.filters.SET_DATA_FROM({
                date: new Date(),
                isDataPossiblyCached,
            }),
        );
    }

    function emitChartRenderError(error: AppError) {
        store$.action$.next(AppAction.portlet.CHART_RENDER_ERROR(error));
    }

    const lazyComponent = () =>
        import('./AnyChart.svelte')
            .then(module => module.default)
            .catch((err: unknown) => {
                $service.errorMonitoringService.captureError(err);
                return Promise.reject(err);
            });
</script>

<Lazy
    {lazyComponent}
    {...anychartConfig}
    {formatters}
    {seriesVisibility}
    onChartRendered={emitChartRenderedAction}
    onDataFromChange={emitSetDataFromAction}
    onChartError={emitChartRenderError}
    listener={handleChartEvents}
    {dispatchPerformanceDataTimeChangeEvent}
    {isDataConsistent}
/>
